import { useState, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { TrendingUp, RotateCcw, Send, Loader2 } from "lucide-react";

const TAG_COLORS = {
  urgent: "bg-red-100 text-red-700",
  high_risk: "bg-amber-100 text-amber-700",
  VIP: "bg-purple-100 text-purple-700",
  follow_up: "bg-blue-100 text-blue-700",
};

const BREAKDOWN_ITEMS = [
  { key: "base_move_charge", label: "Base Move" },
  { key: "distance_adjustment", label: "Distance" },
  { key: "access_adjustment", label: "Access" },
  { key: "packing_addon", label: "Packing" },
  { key: "dismantling_addon", label: "Dismantling" },
  { key: "storage_addon", label: "Storage" },
  { key: "specialist_addon", label: "Specialist" },
  { key: "time_surcharge", label: "Surcharges" },
];

export const QuoteModal = ({ quote, editMode, updating, updateData, setUpdateData, onSave, onReactivate, onSendFollowUp, getStatusColor, statuses }) => {
  const visibleBreakdownItems = useMemo(() => {
    if (!quote.pricing_breakdown) return [];
    return BREAKDOWN_ITEMS.filter((item) => {
      const bd = quote.pricing_breakdown[item.key];
      return bd && (bd.cost > 0 || bd.customer_price > 0);
    });
  }, [quote.pricing_breakdown]);

  return (
  <div className="space-y-5">
    {/* Expired Banner */}
    {quote.status === "expired" && (
      <div className="bg-slate-100 border border-slate-300 p-4 rounded" data-testid="expired-banner">
        <div className="text-sm font-medium text-slate-600 mb-3">
          This quote expired {quote.expired_at ? `on ${new Date(quote.expired_at).toLocaleDateString()}` : "after 7 days of inactivity"}.
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => onReactivate(quote.quote_id)}
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
            data-testid="reactivate-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reactivate Quote
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSendFollowUp(quote.quote_id)}
            data-testid="send-followup-btn"
          >
            <Send className="w-4 h-4 mr-1.5" /> Send Follow-Up
          </Button>
        </div>
      </div>
    )}
    {/* Customer */}
    <div className="bg-slate-50 p-4 rounded">
      <h3 className="font-semibold text-[#0F172A] mb-2 text-sm">Customer</h3>
      <div className="text-sm space-y-1">
        <div><strong>Name:</strong> {quote.customer?.name}</div>
        <div><strong>Email:</strong> {quote.customer?.email}</div>
        <div><strong>Phone:</strong> {quote.customer?.phone}</div>
      </div>
    </div>

    {/* Move Details */}
    <div className="grid grid-cols-2 gap-3">
      <div><div className="text-xs text-slate-500 uppercase mb-1">From</div><div className="font-medium text-sm">{quote.from_postcode}</div></div>
      <div><div className="text-xs text-slate-500 uppercase mb-1">To</div><div className="font-medium text-sm">{quote.to_postcode}</div></div>
      <div><div className="text-xs text-slate-500 uppercase mb-1">Move Date</div><div className="font-medium text-sm">{quote.move_date}</div></div>
      <div><div className="text-xs text-slate-500 uppercase mb-1">Property</div><div className="font-medium text-sm">{quote.bedrooms} bed {quote.property_type}</div></div>
    </div>

    {/* Services */}
    <div>
      <div className="text-xs text-slate-500 uppercase mb-2">Services</div>
      <div className="flex flex-wrap gap-2">
        {quote.packing_required && <span className="px-2 py-1 bg-slate-100 text-xs rounded">Packing</span>}
        {quote.dismantling_required && <span className="px-2 py-1 bg-slate-100 text-xs rounded">Dismantling</span>}
        {quote.storage_required && <span className="px-2 py-1 bg-slate-100 text-xs rounded">Storage ({quote.storage_weeks}w)</span>}
        {quote.specialist_items?.length > 0 && (
          <span className="px-2 py-1 bg-orange-100 text-xs rounded">Specialist: {quote.specialist_items.join(", ")}</span>
        )}
        {quote.survey_required && <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded font-medium">Survey Required</span>}
      </div>
    </div>

    {/* Tags */}
    <div>
      <div className="text-xs text-slate-500 uppercase mb-2">Tags</div>
      <div className="flex flex-wrap gap-1">
        {(quote.tags || []).map((t) => (
          <span key={t} className={`text-xs px-2 py-1 rounded font-medium ${TAG_COLORS[t] || "bg-slate-100"}`}>{t}</span>
        ))}
        {(!quote.tags || quote.tags.length === 0) && <span className="text-xs text-slate-400">No tags</span>}
      </div>
    </div>

    {/* Pricing */}
    <div className="bg-[#0F172A] p-4 rounded text-white">
      <div className="flex justify-between items-center">
        <span className="text-sm">Customer Price</span>
        <span className="text-xl font-bold" data-testid="modal-customer-price">
          £{(quote.final_price || quote.customer_price || quote.estimated_price || 0).toFixed(2)}
        </span>
      </div>
      {quote.internal_cost != null && (
        <div className="mt-3 pt-3 border-t border-slate-700 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Internal Cost</span>
            <span data-testid="modal-internal-cost">£{quote.internal_cost?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Margin ({quote.margin_percent?.toFixed(0)}%)</span>
            <span className="text-green-400" data-testid="modal-margin">£{quote.margin_amount?.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>

    {/* Breakdown */}
    {quote.pricing_breakdown && visibleBreakdownItems.length > 0 && (
      <div className="border border-slate-200 rounded overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#FF5A1F]" />
          <span className="font-semibold text-xs text-[#0F172A]">Price Breakdown (Internal / Customer)</span>
        </div>
        <div className="divide-y divide-slate-100 text-sm">
          {visibleBreakdownItems.map((item) => {
            const bd = quote.pricing_breakdown[item.key];
            return (
              <div key={item.key} className="px-4 py-2 flex justify-between items-center">
                <span className="text-slate-600">{item.label}</span>
                <div className="flex gap-4 text-right">
                  <span className="text-slate-400 w-20">£{bd.cost?.toFixed(2)}</span>
                  <span className="font-medium text-[#0F172A] w-20">£{bd.customer_price?.toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}

    {/* Edit Form */}
    {editMode && (
      <div className="space-y-4 pt-4 border-t">
        <div>
          <label className="text-xs text-slate-500 uppercase mb-1 block">Status</label>
          <Select value={updateData.status} onValueChange={(v) => setUpdateData((prev) => ({ ...prev, status: v }))}>
            <SelectTrigger data-testid="edit-quote-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase mb-1 block">Final Price (£)</label>
          <Input
            type="number"
            step="0.01"
            value={updateData.final_price}
            onChange={(e) => setUpdateData((prev) => ({ ...prev, final_price: e.target.value }))}
            placeholder="Leave blank to use estimated"
            data-testid="edit-quote-price"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase mb-1 block">Add Note</label>
          <Textarea
            value={updateData.notes}
            onChange={(e) => setUpdateData((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Add a note..."
            data-testid="edit-quote-notes"
          />
        </div>
        <Button onClick={onSave} disabled={updating} className="w-full btn-primary" data-testid="save-quote-btn">
          {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
        </Button>
      </div>
    )}
  </div>
  );
};
